import { CERTIFICATE_TYPE_LABELS } from '@/entities/certificate';
import { OnboardingProcessDetails } from '@/entities/onboarding-status';

export const SUPPORT_EMAIL = 'contacto@cybersign.gt';

// Whether a user with a request in review may start a second one (behind a
// warning). Blocked by product decision: one request at a time.
export const ALLOW_NEW_REQUEST_WHILE_IN_REVIEW = false;

/** mailto: link to support that names the request in the subject. */
export function buildSupportMailto(onboardingUlid: string): string {
	const subject = `Solicitud de firma en revisión - ${onboardingUlid}`;
	const body = `Hola, necesito ayuda con mi solicitud de firma ${onboardingUlid}.`;
	return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
		subject,
	)}&body=${encodeURIComponent(body)}`;
}

// Keys are the backend's payment_processor values, lower-cased.
const PAYMENT_PROCESSOR_LABELS: Record<string, string> = {
	recurrente: 'Tarjeta',
	promo_code: 'Código promocional',
};

const asText = (value: unknown) =>
	typeof value === 'string' && value.trim() ? value : null;

function formatDate(value: unknown): string | null {
	const text = asText(value);
	if (!text) return null;
	const date = new Date(text);
	if (isNaN(date.getTime())) return text;
	return date.toLocaleDateString('es-GT', {
		day: 'numeric',
		month: 'long',
		year: 'numeric',
		// A bare date parses as UTC midnight; keep it from shifting a day back.
		...(/^\d{4}-\d{2}-\d{2}$/.test(text) && { timeZone: 'UTC' }),
	});
}

/** Label/value rows for the details the backend sent; missing ones are left out. */
export function describeProcess(
	details: OnboardingProcessDetails | null,
): Array<{ label: string; value: string }> {
	if (!details) return [];

	const certType = asText(details.cert_type);
	const processor = asText(details.payment_processor);
	const rows: Array<{ label: string; value: string | null }> = [
		{
			label: 'Tipo de certificado',
			value: certType
				? (CERTIFICATE_TYPE_LABELS[certType] ?? certType)
				: null,
		},
		{ label: 'Fecha de solicitud', value: formatDate(details.created_at) },
		{ label: 'Fecha de pago', value: formatDate(details.paid_at) },
		{
			label: 'Método de pago',
			value: processor
				? (PAYMENT_PROCESSOR_LABELS[processor.toLowerCase()] ??
					processor)
				: null,
		},
		{
			label: 'Verificación de identidad',
			value: formatDate(details.kyc_completed_at),
		},
	];

	return rows.filter(
		(row): row is { label: string; value: string } => row.value !== null,
	);
}
